// Name → zod schema registry. The route catalog (routes.ts) references request/response schemas by these names so
// the server can validate generically and BUILD-SPEC-api.md can print them. Names are unique across areas (tested).
import type { z } from 'zod';
import { CommonFilterQuery, CursorQuery, ErrorEnvelope, ExportJsonFile, ExportMeta, ExportRequest, JobHandle, NoBody, OkResponse, PageQuery, UndoParams, UndoResponse } from './common.js';
import { authSchemas } from './api/auth.js';
import { homeSchemas } from './api/home.js';
import { recordsSchemas } from './api/records.js';
import { statsSchemas } from './api/stats.js';
import { peopleSchemas } from './api/people.js';
import { connectSchemas } from './api/connect.js';
import { clientAuthorizationSchemas } from './api/client-authorization.js';
import { glossarySchemas } from './api/glossary.js';
import { meSchemas } from './api/me.js';
import { adminRolesSchemas } from './api/admin-roles.js';
import { adminPolicySchemas } from './api/admin-policy.js';
import { adminAuditSchemas } from './api/admin-audit.js';
import { adminCollectionSchemas } from './api/admin-collection.js';
import { adminCostSchemas } from './api/admin-cost.js';
import { notificationsSchemas } from './api/notifications.js';
import { searchSchemas } from './api/search.js';
import { ingestSchemas } from './api/ingest.js';
import { gatewaySchemas } from './api/gateway.js';
import { gatewayModelSchemas } from './api/gateway-models.js';

export const commonSchemas = { OkResponse, NoBody, ErrorEnvelope, ExportRequest, ExportMeta, ExportJsonFile, JobHandle, CommonFilterQuery, PageQuery, CursorQuery, UndoParams, UndoResponse } as const;

/** Schema groups per area, in BUILD-SPEC order. */
export const SCHEMA_GROUPS = {
  common: commonSchemas,
  auth: authSchemas,
  home: homeSchemas,
  records: recordsSchemas,
  stats: statsSchemas,
  people: peopleSchemas,
  connect: { ...connectSchemas, ...clientAuthorizationSchemas, ...gatewayModelSchemas },
  glossary: glossarySchemas,
  me: meSchemas,
  'admin-roles': adminRolesSchemas,
  'admin-policy': adminPolicySchemas,
  'admin-audit': adminAuditSchemas,
  'admin-collection': adminCollectionSchemas,
  'admin-cost': adminCostSchemas,
  notifications: notificationsSchemas,
  search: searchSchemas,
  ingest: ingestSchemas,
  gateway: gatewaySchemas,
} as const;

export const SCHEMAS = {
  ...commonSchemas,
  ...authSchemas,
  ...homeSchemas,
  ...recordsSchemas,
  ...statsSchemas,
  ...peopleSchemas,
  ...connectSchemas,
  ...clientAuthorizationSchemas,
  ...gatewayModelSchemas,
  ...glossarySchemas,
  ...meSchemas,
  ...adminRolesSchemas,
  ...adminPolicySchemas,
  ...adminAuditSchemas,
  ...adminCollectionSchemas,
  ...adminCostSchemas,
  ...notificationsSchemas,
  ...searchSchemas,
  ...ingestSchemas,
  ...gatewaySchemas,
} as const;
export type SchemaName = keyof typeof SCHEMAS;
export const schemaOf = (name: SchemaName): z.ZodType => SCHEMAS[name] as unknown as z.ZodType;
